import { ResendCode } from "@faire/retailer-visitor-shared/components/AppSignIn/components/__internal__/ResendCode";
import { ContinueToNextViewButton } from "@faire/retailer-visitor-shared/components/AppSignIn/components/ContinueToNextViewButton";
import { convertErrorToLoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/convertErrorToLoginError";
import { LoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/LoginError";
import { LoginWrapper } from "@faire/retailer-visitor-shared/components/AppSignIn/components/styled";
import {
  isNumericOrBlank,
  isNumericOrBlankCode,
  VALID_CODE_LENGTH,
} from "@faire/retailer-visitor-shared/components/AppSignIn/components/VerificationInputUtils";
import { View } from "@faire/retailer-visitor-shared/components/AppSignIn/components/View";
import { useSignInEventTracker } from "@faire/retailer-visitor-shared/components/AppSignIn/useSignInEventTracker";
import { VerificationInput } from "@faire/retailer-visitor-shared/components/VerificationInput/VerificationInput";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import login from "@faire/web-api--source/endpoints/www/api/v2/users/login/post";
import { trackRetailerPortalLoginModalLoggedInWithCode } from "@faire/web-api--source/events/retailerPortal/actionCategoryOther/loginModalLoggedInWithCode";
import { trackRetailerPortalLoginModalLoginWithCode } from "@faire/web-api--source/events/retailerPortal/actionCategoryOther/loginModalLoginWithCode";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { MfaMethod } from "@faire/web-api--source/indigofair/data/MfaMethod";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { FC, useRef, useState } from "react";

type EnterCodeResult = {
  nextView: View;
  loggedInUser: IUser;
};

interface IProps {
  email: string;
  userToken?: string;
  mfaTitle?: string;
  mfaDescription?: string;
  mfaMethod?: keyof typeof MfaMethod;
  onComplete: (result: EnterCodeResult) => void;
  onLogin: (user: IUser) => void;
  password?: string;
}

export const EnterMFACodeView: FC<IProps> = ({
  email,
  userToken,
  onComplete,
  onLogin,
  mfaTitle,
  mfaDescription,
  mfaMethod,
  password,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [showResend, setShowResend] = useState(true);
  const [isLoadingAutoLogin, setIsLoadingAutoLogin] = useState(false);
  const code = useRef("");
  const [error, setError] = useState<LoginError>();
  const [codeInputError, setCodeInputError] = useState(false);
  const { isActive, ssoToken } = useSSOStore();
  const [verificationInputErrorMessage, setVerificationInputErrorMessage] =
    useState<string>();
  const { trackFieldValue } = useSignInEventTracker("ENTER_CODE");

  const onContinue = async () => {
    trackRetailerPortalLoginModalLoginWithCode();
    setError(undefined);
    setIsLoadingAutoLogin(true);

    try {
      const { user } = await login(
        isActive
          ? ILoginRequest.build({
              mfa_code: code.current,
              sso_token: ssoToken,
              password: password,
              enable_mfa_method_selection: true,
            })
          : ILoginRequest.build({
              mfa_code: code.current,
              email_address: email,
              user_token: !email ? userToken : undefined,
              password: password,
              enable_mfa_method_selection: true,
            })
      );

      if (!user) {
        setError(
          new LoginError(
            strictLocalize({
              id: "bw8Bnw",
              defaultMessage: "Invalid credentials",
              description: {
                text: "Email or password that was entered is incorrect",
              },
            })
          )
        );
        return;
      }

      const nextView = user.missing_password
        ? View.CREATE_PASSWORD
        : View.LOADING;
      if (nextView === View.LOADING) {
        trackRetailerPortalLoginModalLoggedInWithCode();
        onLogin(user);
      }
      onComplete({ nextView, loggedInUser: user });
    } catch (e) {
      setCodeInputError(true);
      setVerificationInputErrorMessage(
        strictLocalize({
          id: "RNH93U",
          defaultMessage: "Invalid code, please try again",
          description: {
            text: "Verifiction code that was entered is incorrect",
            img: "https://cdn.faire.com/fastly/cad265ff240f88e65f74b31ab9eb0fdf8c8c55bcdd39f08b4b79f423110d5ac1.png",
          },
        })
      );
    } finally {
      setIsLoadingAutoLogin(false);
    }
  };

  const isValidCode = (): boolean =>
    code.current.length === VALID_CODE_LENGTH &&
    !verificationInputErrorMessage &&
    !codeInputError;

  const handleVerificationCodeInputChange = (
    codeValue: string,
    slotValue: string
  ) => {
    code.current = codeValue;
    setCodeInputError(false);
    trackFieldValue("verificationCode", codeValue);

    if (!isNumericOrBlankCode(slotValue)) {
      setVerificationInputErrorMessage(
        strictLocalize({
          id: "Wy3Kfu",
          defaultMessage: "Enter numbers only.",
          description: {
            text: "Email or password that was entered is incorrect",
            img: "https://cdn.faire.com/fastly/52a1bf8c774d8b3fed237efa2a26b2870aeac3cf261e66a7694e6083f7a0398e.png",
          },
        })
      );
    } else if (isNumericOrBlankCode(code.current)) {
      setVerificationInputErrorMessage(undefined);
      if (isValidCode()) {
        onContinue();
      }
    }
  };

  const onResend = async () => {
    try {
      await login(
        ILoginRequest.build({
          email_address: email,
          user_token: !email ? userToken : undefined,
          mfa_method: mfaMethod,
          resend: true,
        })
      );
      setShowResend(false);
      setError(undefined);
    } catch (e) {
      setError(convertErrorToLoginError(e, strictLocalize));
    }
  };

  return (
    <LoginWrapper>
      <Typography variant="displaySSerifRegular">
        {mfaTitle ? (
          mfaTitle
        ) : (
          <StrictLocalMsg
            id="k/hsBX"
            defaultMessage="Enter code"
            description={{
              text: "title for enter code view",
              img: "https://cdn.faire.com/fastly/cad265ff240f88e65f74b31ab9eb0fdf8c8c55bcdd39f08b4b79f423110d5ac1.png",
            }}
          />
        )}
      </Typography>
      <Spacer height="2x" />
      <Typography>
        {mfaDescription ? (
          mfaDescription
        ) : (
          <StrictLocalMsg
            id="cxpBFc"
            defaultMessage="Copy and paste the code in the email we sent to {userTextMessageAddress}"
            description={{
              text: "description for enter code view",
              img: "https://cdn.faire.com/fastly/cad265ff240f88e65f74b31ab9eb0fdf8c8c55bcdd39f08b4b79f423110d5ac1.png",
            }}
            values={{
              userTextMessageAddress: email,
            }}
          />
        )}
      </Typography>
      <Spacer height="3x" />
      <VerificationInput
        validateFn={isNumericOrBlank}
        onChange={handleVerificationCodeInputChange}
        errorMessage={verificationInputErrorMessage ?? error?.message}
        errorAll={codeInputError}
        isValidInput={isValidCode()}
        value={code.current}
        ariaLabel={
          mfaMethod === MfaMethod.SMS
            ? strictLocalize({
                id: "dB/6on",
                defaultMessage: "Enter SMS Code",
                description: {
                  text: "Accessible name for prompt for user to enter a code that was sent to their phone number",
                },
              })
            : strictLocalize({
                id: "e4w9ay",
                defaultMessage: "Enter Email Code",
                description: {
                  text: "Accessible name for prompt for user to enter a code that was sent to their email address",
                },
              })
        }
      />
      <Spacer height="3x" />
      <ContinueToNextViewButton
        onClick={onContinue}
        isLoading={isLoadingAutoLogin}
        disabled={!isValidCode()}
      />
      <Spacer height="3x" />
      <ResendCode
        error={error}
        onResend={onResend}
        showResend={showResend}
        mfaMethod={mfaMethod}
      />
    </LoginWrapper>
  );
};
